import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import type { StageMessage } from './shared';
import { useProtectionActivity } from './use-protection-activity';

/**
 * "V3" banner — corn only, advisor-facing.
 *
 * Shown to advisor-type users when an active corn crop monitoring is in the V3 or V4 stage
 * (`phenology === 'emergence'` with `vegetativeStage` 3 or 4) and no fungicide/insecticide
 * application or recommendation is registered yet. It nudges protecting yield against
 * pests/diseases. (Same protection message as V2's second case, but with no NSmart step.)
 *
 * Returns `null` outside V3/V4, for non-advisor users, or once some fungicide/insecticide
 * activity is registered.
 */
export const useV3StageMessage = ({
    cropMonitoring,
    field,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();

    const corn = cropMonitoring?.cornCropMonitoring;
    const isCornV3 =
        Boolean(cropMonitoring?.active) &&
        cropMonitoring?.crop === 'corn' &&
        corn?.phenology === 'emergence' &&
        [3, 4].includes(corn?.vegetativeStage ?? 0);

    const showCandidate = isValidatedBayerUserType && isCornV3;

    const protection = useProtectionActivity({ cropMonitoring, field, enabled: showCandidate });

    if (!showCandidate) return null;

    // Hold until the gating queries resolve, then only prompt while no protection activity exists.
    if (protection.isLoading) return null;
    if (protection.hasActivity) return null;

    return {
        title: _('Ayudá a proteger el rendimiento del cultivo'),
        message: _(
            'Acompañá al productor a detectar plagas y enfermedades a tiempo y recomendá el producto para controlarlas.'
        ),
        secondaryAction: { label: _('Registrar aplicación'), action: 'register-application' },
        primaryAction: { label: _('Generar recomendación'), action: 'generate-cp-recommendation' },
    };
};
